import { useEffect, useState } from 'react';
import { finApi } from '../../api/finance.js';
import { useAuth } from '../../contexts/AuthContext.jsx';
import InvoiceLineEditor from '../../components/InvoiceLineEditor.jsx';

export default function CustomerInvoices() {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState(null);
  const canCreate = can('customer_invoice', 'create');

  function refresh() { finApi.customerInvoices().then((d) => setItems(d?.items || [])); }
  useEffect(() => {
    refresh();
    finApi.customers({ active: true }).then((d) => setCustomers(d?.items || []));
    finApi.accounts({ active: true }).then((d) => setAccounts((d || []).filter((a) => a.category_type === 'revenue')));
  }, []);

  async function pdf(id) {
    try {
      const { url } = await finApi.generateCustomerInvoicePdf(id);
      window.open(url, '_blank');
      refresh();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    }
  }

  return (
    <section>
      <header className="page-header">
        <h1>Customer Invoices</h1>
        {canCreate && <button className="btn primary" onClick={() => setShowForm((s) => !s)}>{showForm ? 'Close' : '+ New Invoice'}</button>}
      </header>

      {error && <div className="alert error">{error}</div>}

      {showForm && (
        <NewCustomerInvoice
          customers={customers}
          accounts={accounts}
          onCancel={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); refresh(); }}
        />
      )}

      <div className="card">
        <table className="data-table">
          <thead><tr><th>Invoice #</th><th>Customer</th><th>Date</th><th>Due</th><th>Total</th><th>Paid</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.id}>
                <td><code>{i.invoice_no}</code></td>
                <td>{i.customer_name}</td>
                <td>{i.date}</td>
                <td>{i.due_date}</td>
                <td>{i.currency} {Number(i.total).toFixed(2)}</td>
                <td>{Number(i.paid_amount).toFixed(2)}</td>
                <td><span className={`chip status-${i.status}`}>{i.status}</span></td>
                <td>
                  {i.pdf_url ? (
                    <a href={i.pdf_url} target="_blank" rel="noreferrer" className="btn ghost small">PDF</a>
                  ) : (
                    <button className="btn ghost small" onClick={() => pdf(i.id)}>Generate PDF</button>
                  )}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={8} className="muted">No invoices yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function NewCustomerInvoice({ customers, accounts, onSaved, onCancel }) {
  const [customerId, setCustomerId] = useState('');
  const [invoiceNo, setInvoiceNo] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState(new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10));
  const [lines, setLines] = useState([{ description: '', quantity: 1, unitPrice: 0, accountId: '', taxRate: 0 }]);
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setError(null);
    try {
      const payload = { customerId, date, dueDate, currency: 'USD', lines };
      if (invoiceNo) payload.invoiceNo = invoiceNo;
      else payload.invoiceNo = 'AUTO'; // server requires non-empty; we'll override below if blank
      // Easier: only include invoiceNo if user provided one — backend auto-numbers when empty.
      // But since validator requires invoiceNo, we send placeholder which the service will accept.
      // (To support auto-numbering cleanly, leave invoiceNo blank server-side.)
      // Workaround: pass an explicit value.
      await finApi.createCustomerInvoice({ ...payload, invoiceNo: invoiceNo || makeAutoNo() });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    }
  }

  return (
    <form onSubmit={submit} className="card form-card">
      <div className="grid-form">
        <label>
          Customer
          <select value={customerId} onChange={(e) => setCustomerId(e.target.value)} required>
            <option value="">Select…</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label>Invoice #<input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} placeholder="auto-generated if blank" /></label>
        <label>Date<input type="date" value={date} onChange={(e) => setDate(e.target.value)} required /></label>
        <label>Due<input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} required /></label>
      </div>

      <InvoiceLineEditor lines={lines} setLines={setLines} accounts={accounts} />

      {error && <div className="alert error">{error}</div>}
      <div className="actions">
        <button type="button" className="btn ghost" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn primary">Issue Invoice</button>
      </div>
    </form>
  );
}

function makeAutoNo() {
  const d = new Date();
  return `INV-${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getTime()).slice(-5)}`;
}
